import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import GradientStrip from "@/components/GradientStrip";
import ClientsMarquee from "@/components/ClientsMarquee";
import { api } from "@/lib/config";
import { useT } from "@/lib/i18n";
import { ArrowRight } from "lucide-react";

export default function References() {
  const t = useT();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/references").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  return (
    <Layout headerVariant="dark">
      <section data-testid="references-hero" className="bg-brand-deep text-white pt-40 md:pt-48 pb-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="section-num text-accent-pink fade-up">{t("references.eyebrow")}</p>
          <h1 className="display text-5xl md:text-7xl lg:text-8xl leading-[0.95] mt-8 max-w-5xl fade-up-2">
            {t("references.titleA")}<em className="not-italic text-accent-yellow">{t("references.titleAccent")}</em>
          </h1>
          <p className="text-white/90 text-lg md:text-xl leading-relaxed mt-10 max-w-3xl fade-up-3">
            {t("references.lede")}
          </p>
        </div>
        <GradientStrip />
      </section>

      <section className="bg-paper py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          {items.length === 0 && (
            <div className="border border-ink/10 p-12 text-center">
              <p className="display text-2xl text-ink/70">{t("references.empty")}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {items.map((r, i) => (
              <article
                key={r.id}
                data-testid={`reference-${r.id}`}
                className="group bg-paper border border-ink/10 overflow-hidden hover:border-brand/30 transition-all duration-500"
              >
                <div className="relative h-72 lg:h-80 bg-brand-deep overflow-hidden">
                  {r.image && (
                    <img
                      src={r.image}
                      alt={r.client_name}
                      className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-1000"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-deep via-brand-deep/40 to-transparent" />
                  {r.logo && (
                    <div className="absolute top-6 left-6 bg-white/10 backdrop-blur-md px-4 py-3 rounded">
                      <img src={r.logo} alt={r.client_name} className="h-9 max-w-[180px] object-contain opacity-95"/>
                    </div>
                  )}
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-accent-pink mb-2">{r.client_name}</p>
                    <h2 className="display text-2xl md:text-3xl leading-tight">{r.headline}</h2>
                  </div>
                </div>
                <div className="p-8 md:p-10">
                  <p className="text-[15px] leading-relaxed text-ink/85 whitespace-pre-line">
                    {r.body}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <ClientsMarquee />

      <section className="bg-brand text-white py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="section-num text-white/90">{t("references.ctaEyebrow")}</p>
            <h2 className="display text-3xl md:text-5xl leading-tight mt-6 max-w-3xl">
              {t("references.ctaTitle")}
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" data-testid="references-cta" className="btn-pill bg-white text-brand hover:bg-paper-2">
              {t("common.cta.bookCall")} <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
